import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminOrdersApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { Loading } from '../components/Loading';
import { getNextStatus, canCancelOrder, getNextStatusLabel, getStatusLabel } from '../utils/orderStatus';
import type { Order, OrderStatus } from '../types';
import './AdminOrdersPage.css';

const ORDER_STATUSES: { value: OrderStatus; label: string }[] = [
  { value: 'pending', label: 'Ожидает оплаты' },
  { value: 'paid', label: 'Оплачен' },
  { value: 'preparing', label: 'Собирается' },
  { value: 'delivering', label: 'В доставке' },
  { value: 'completed', label: 'Завершен' },
  { value: 'cancelled', label: 'Отменен' },
];

const ITEMS_PER_PAGE = 20;

export const AdminOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { tg } = useTelegram();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);

  // Filters
  const [selectedStatuses, setSelectedStatuses] = useState<OrderStatus[]>(
    ORDER_STATUSES.filter(s => s.value !== 'completed').map(s => s.value)
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

   
   
  useEffect(() => {
    // Show back button
    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(handleBack);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg]);

  useEffect(() => {
    loadOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses, searchQuery, currentPage]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: {
        status?: OrderStatus[];
        search?: string;
        limit: number;
        offset: number;
      } = {
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        search: searchQuery || undefined,
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      };

      const response = await adminOrdersApi.getOrders(params);
      setOrders(response.orders || []);
      setTotalOrders(response.total || 0);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить заказы');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin');
  };

  const handleOrderClick = (orderId: number) => {
    navigate(`/admin/orders/${orderId}`);
  };

  const handleStatusToggle = (status: OrderStatus) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
    setCurrentPage(1); // Reset to first page
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMoveToNextStatus = async (e: React.MouseEvent, order: Order) => {
    e.stopPropagation(); // Prevent opening order details

    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) return;

    const nextStatusLabel = getNextStatusLabel(order.status);

    tg?.showConfirm(`Перевести заказ #${order.id} в статус "${nextStatusLabel}"?`, async (confirmed) => {
      if (!confirmed) return;

      try {
        setUpdatingOrderId(order.id);
        await adminOrdersApi.updateStatus(order.id, {
          status: nextStatus,
        });

        // Reload orders to get updated data
        await loadOrders();

        tg?.showAlert('Статус заказа обновлен');
      } catch (err: unknown) {
        tg?.showAlert(err instanceof Error ? err.message : 'Ошибка обновления статуса');
      } finally {
        setUpdatingOrderId(null);
      }
    });
  };

  const handleCancelOrder = async (e: React.MouseEvent, order: Order) => {
    e.stopPropagation(); // Prevent opening order details

    if (!canCancelOrder(order.status)) {
      tg?.showAlert('Заказ нельзя отменить на данном этапе');
      return;
    }

    tg?.showConfirm(`Вы уверены, что хотите отменить заказ #${order.id}?`, async (confirmed) => {
      if (!confirmed) return;

      try {
        setUpdatingOrderId(order.id);
        await adminOrdersApi.updateStatus(order.id, {
          status: 'cancelled',
        });

        // Reload orders to get updated data
        await loadOrders();

        tg?.showAlert('Заказ отменен');
      } catch (err: unknown) {
        tg?.showAlert(err instanceof Error ? err.message : 'Ошибка отмены заказа');
      } finally {
        setUpdatingOrderId(null);
      }
    });
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(0)} ₽`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'status-pending',
      paid: 'status-paid',
      preparing: 'status-preparing',
      delivering: 'status-delivering',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return colors[status] || '';
  };

  const totalPages = Math.ceil(totalOrders / ITEMS_PER_PAGE);

  return (
    <div className="page admin-orders-page">
      <div className="admin-orders-header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="admin-orders-title">Заказы</h1>
        <div className="orders-count">{totalOrders}</div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        {/* Search */}
        <div className="search-box">
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Поиск по номеру заказа, телефону..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => handleSearchChange('')}>
              ×
            </button>
          )}
        </div>

        {/* Status filters */}
        <div className="filter-group">
          <label className="filter-label">Статус заказа:</label>
          <div className="filter-chips">
            {ORDER_STATUSES.map(status => (
              <button
                key={status.value}
                className={`filter-chip ${selectedStatuses.includes(status.value) ? 'active' : ''}`}
                onClick={() => handleStatusToggle(status.value)}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Orders list */}
      {loading ? (
        <Loading />
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-button" onClick={loadOrders}>
            Попробовать снова
          </button>
        </div>
      ) : orders.length === 0 ? (
        <div className="empty-orders">
          <div className="empty-icon">📦</div>
          <h3>Заказы не найдены</h3>
          <p>Попробуйте изменить фильтры</p>
        </div>
      ) : (
        <>
          <div className="orders-list">
            {orders.map(order => (
              <div
                key={order.id}
                className="admin-order-card"
                onClick={() => handleOrderClick(order.id)}
              >
                <div className="order-card-header">
                  <span className="order-id">#{order.id}</span>
                  <div className="order-badges">
                    <span className={`order-status-badge ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                </div>

                <div className="order-card-info">
                  <div className="info-row">
                    <span className="info-label">Дата:</span>
                    <span className="info-value">{formatDate(order.created_at)}</span>
                  </div>
                  {order.items && order.items.length > 0 && (
                    <div className="info-row">
                      <span className="info-label">Товаров:</span>
                      <span className="info-value">{order.items.length} шт.</span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="info-label">Сумма:</span>
                    <span className="info-value total-amount">
                      {formatPrice(order.total_amount + order.delivery_fee)}
                    </span>
                  </div>
                </div>

                {/* Status action buttons */}
                {(getNextStatus(order.status) || canCancelOrder(order.status)) && (
                  <div className="order-card-actions">
                    {getNextStatus(order.status) && (
                      <button
                        className="card-action-btn next-status"
                        onClick={(e) => handleMoveToNextStatus(e, order)}
                        disabled={updatingOrderId === order.id}
                      >
                        {updatingOrderId === order.id ? '...' : `→ ${getNextStatusLabel(order.status)}`}
                      </button>
                    )}
                    {canCancelOrder(order.status) && (
                      <button
                        className="card-action-btn cancel-order"
                        onClick={(e) => handleCancelOrder(e, order)}
                        disabled={updatingOrderId === order.id}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                ← Назад
              </button>

              <div className="pagination-info">
                Страница {currentPage} из {totalPages}
              </div>

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Вперед →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
